import { useRef, useState } from "react";

interface GalleryProps {
  images: string[];
  alt: string;
}

/**
 * Gallery — свайп-галерея карточки товара с точками-индикаторами (docs/07-miniapp-ux.md §2.3).
 * Свайп — обычный горизонтальный scroll-snap (без сторонней библиотеки жестов, п.4 ТЗ);
 * точки дублируют навигацию для клика и синхронизируются со скроллом.
 */
export function Gallery({ images, alt }: GalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  if (images.length === 0) {
    return <div className="aspect-square bg-surface" />;
  }

  function handleScroll() {
    const container = containerRef.current;
    if (!container || container.clientWidth === 0) {
      return;
    }
    setActiveIndex(Math.round(container.scrollLeft / container.clientWidth));
  }

  function goTo(index: number) {
    setActiveIndex(index);
    const container = containerRef.current;
    // jsdom (тесты) не реализует Element.scrollTo — в браузере он есть всегда.
    if (container && typeof container.scrollTo === "function") {
      container.scrollTo({ left: index * container.clientWidth, behavior: "smooth" });
    }
  }

  return (
    <div>
      <div ref={containerRef} onScroll={handleScroll} className="flex snap-x snap-mandatory overflow-x-auto">
        {images.map((src) => (
          <img key={src} src={src} alt={alt} loading="lazy" className="aspect-square w-full shrink-0 snap-center object-cover" />
        ))}
      </div>
      {images.length > 1 ? (
        <div className="flex justify-center gap-1.5 py-2">
          {images.map((src, index) => (
            <button
              key={src}
              type="button"
              aria-label={`Фото ${index + 1}`}
              aria-current={index === activeIndex}
              onClick={() => goTo(index)}
              className={`h-1.5 w-1.5 rounded-full ${index === activeIndex ? "bg-accent" : "bg-hint"}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
